<script setup lang="ts">
import type { PatternCell, PatternRail } from '~/utils/auth-pattern';
import { useTemplateRef } from 'vue';
import { useAuthPatternWalk } from '~/composables/use-auth-pattern-walk';
import { patternRails, VIEWBOX } from '~/utils/auth-pattern';

const pattern = useTemplateRef<SVGSVGElement>('pattern');

const {
	highlightedKey,
	isEntering,
	isCarrying,
	followPointer,
	handlePointerEnter,
	handlePointerLeave,
	handleKeydown,
	handleBlur,
	selectCell,
	handleRailIteration,
	handleEntryAnimationEnd,
} = useAuthPatternWalk(pattern);

const viewBox = `0 0 ${VIEWBOX.width} ${VIEWBOX.height}`;

function railStyle(rail: PatternRail) {
	return { '--rail-shift-x': `${rail.shift.x}px`, '--rail-shift-y': `${rail.shift.y}px` };
}

function entryStyle(cell: PatternCell) {
	return { '--entry-delay': `${cell.entryDelay}ms` };
}
</script>

<template>
	<div
		class="
			absolute inset-0 outline-none
			focus-visible:outline-2 focus-visible:outline-offset-[-14px] focus-visible:outline-white/86
		"
		role="region"
		tabindex="0"
		:aria-label="$t('auth.login.pattern_label')"
		@pointerenter="handlePointerEnter"
		@pointermove="followPointer"
		@pointerleave="handlePointerLeave"
		@keydown="handleKeydown"
		@blur="handleBlur"
	>
		<svg
			ref="pattern"
			class="block size-full"
			:viewBox
			preserveAspectRatio="xMidYMid slice"
			aria-hidden="true"
		>
			<g
				v-for="rail in patternRails"
				:key="rail.row"
				class="animate-[auth-pattern-rail_40s_linear_infinite] motion-reduce:animate-none"
				:style="railStyle(rail)"
				:data-pattern-rail="rail.row"
				@animationiteration.self="handleRailIteration(rail)"
			>
				<g
					v-for="cell in rail.cells"
					:key="cell.key"
					:transform="cell.transform"
					data-pattern-cell
					:data-row="cell.row"
					:data-column="cell.column"
					:data-highlighted="cell.key === highlightedKey || undefined"
					@click="selectCell(cell)"
				>
					<g
						class="origin-center transform-fill motion-reduce:animate-none"
						:class="{
							'animate-[auth-pattern-mouth-enter_440ms_cubic-bezier(0.215,0.61,0.355,1)_var(--entry-delay)_both] will-change-[transform,opacity]': isEntering,
						}"
						:style="entryStyle(cell)"
						@animationend.self="handleEntryAnimationEnd(cell)"
					>
						<AuthPatternMouth />
						<AuthPatternMouth
							v-if="cell.reachable || cell.key === highlightedKey"
							active
							class="ease-[cubic-bezier(0.645,0.045,0.355,1)] motion-reduce:transition-none"
							:class="[
								isCarrying ? 'transition-none' : 'transition-opacity',
								cell.key === highlightedKey ? 'opacity-100 duration-500' : 'opacity-0 duration-300',
							]"
						/>
					</g>
				</g>
			</g>
		</svg>
	</div>
</template>

<style>
/* Each loop slides a rail exactly one slot, so the restart lands every mouth where its neighbour began. */
@keyframes auth-pattern-rail {
	from {
		transform: translate(0, 0);
	}

	to {
		transform: translate(var(--rail-shift-x), var(--rail-shift-y));
	}
}

@keyframes auth-pattern-mouth-enter {
	from {
		opacity: 0;
		transform: translateY(32px) scale(0.992);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}
</style>
